// "Stone Mountain Park" — a high-volume attraction-parking operator. This is the
// prospect-facing demo: ~1M parking sessions/yr, sold as daily / annual / multi-
// car passes across the park's lots. The finance knobs are tuned so the 30-day
// Accounting/Analytics window reads as a single PEAK SEASON MONTH (~100k sessions,
// ~$2M) — i.e. a believable slice of a 1M-sessions/yr run-rate.
//
// Real published pricing (2026): Daily $20 · second vehicle (same txn) $25 ·
// Annual parking pass $40. Lot names + addresses are kept generic/neutral.

import type { DemoClient } from './types'
import { STONE_MOUNTAIN_LOGO, STONE_MOUNTAIN_ICON } from './stoneMountainLogo'

export const STONE_MOUNTAIN_CLIENT_ID = 'stone-mountain'

const FAC_PARK = 'smp-lots'

export const stoneMountainClient: DemoClient = {
  id: STONE_MOUNTAIN_CLIENT_ID,
  name: 'Stone Mountain Park',
  timezone: 'America/New_York',
  // White-labeled to Stone Mountain's brand lake-blue (their published
  // theme-color) so the console demos in the prospect's own colors.
  accent: '#036089',
  logoUrl: STONE_MOUNTAIN_LOGO,
  iconUrl: STONE_MOUNTAIN_ICON,
  user: { email: 'billy.holt@stonemountainpark.com', name: 'Billy Holt', role: 'manager' },
  contactName: 'Billy Holt',
  contactEmail: 'billy.holt@stonemountainpark.com',
  contactPhone: '(770) 498-5690',
  notes: 'Demo tenant — high-volume attraction parking (daily / annual / multi-car passes).',
  stripeAccountId: 'acct_stoneMtn0001',
  platformFeeFlatCents: 30,
  customerFees: {
    adminFeeCents: 0,
    serviceFeePassThrough: true,
    serviceFeeBps: 290,
    serviceFeeFixedCents: 30,
  },
  city: 'Stone Mountain',
  state: 'GA',
  zipCode: '30083',
  zones: [
    {
      id: 'smp-main-gate', name: 'Main Gate Parking', displayName: 'MG1', zoneNumber: '1',
      type: 'surface', totalSpaces: 3200, transientCapacity: 3100, monthlyCapacity: 100,
      // Daily pass is the product: $20 flat, all-day re-entry → daily max = $20.
      hourlyCents: 500, additionalHourCents: 300,
      rateChoices: [
        { choiceId: '1h', durationMins: 60 }, { choiceId: '2h', durationMins: 120 },
        { choiceId: '3h', durationMins: 180 }, { choiceId: '4h', durationMins: 240 },
        { choiceId: '6h', durationMins: 360 },
      ],
      dailyMaxCents: 2000, eventFlatCents: 3000,
      address: 'Main Gate, Hwy 78', lat: 33.8053, lng: -84.1455,
      description: 'Primary attraction parking — pay-on-entry, all-day re-entry.',
      facilityId: FAC_PARK,
    },
    {
      id: 'smp-lakeside', name: 'Lakeside Overflow Lots', displayName: 'LK1', zoneNumber: '2',
      type: 'surface', totalSpaces: 2400, transientCapacity: 2380, monthlyCapacity: 20,
      hourlyCents: 500, additionalHourCents: 300,
      rateChoices: [
        { choiceId: '1h', durationMins: 60 }, { choiceId: '2h', durationMins: 120 },
        { choiceId: '3h', durationMins: 180 }, { choiceId: '4h', durationMins: 240 },
        { choiceId: '5h', durationMins: 300 }, { choiceId: '6h', durationMins: 360 },
      ],
      operatingHours: Object.fromEntries(Array.from({ length: 7 }, (_, day) => [day, { open: '06:00', close: '22:00' }])),
      dailyMaxCents: 2000, eventFlatCents: 3000,
      address: 'Lakeshore Dr', lat: 33.8090, lng: -84.1390,
      description: 'Weekend & event overflow near the lakefront and event lawn.',
      facilityId: FAC_PARK,
    },
    {
      id: 'smp-marina', name: 'Marina & Campground Lots', displayName: 'MA1', zoneNumber: '3',
      type: 'surface', totalSpaces: 700, transientCapacity: 650, monthlyCapacity: 50,
      hourlyCents: 600, additionalHourCents: 400,
      rateChoices: [
        { choiceId: '1h', durationMins: 60 }, { choiceId: '2h', durationMins: 120 },
        { choiceId: '3h', durationMins: 180 }, { choiceId: '4h', durationMins: 240 },
        { choiceId: '8h', durationMins: 480 }, { choiceId: '24h', durationMins: 1440 },
      ],
      dailyMaxCents: 2000, eventFlatCents: 3000,
      address: 'Marina Dr', lat: 33.8021, lng: -84.1502,
      description: 'Marina, golf and campground parking.',
      facilityId: FAC_PARK,
    },
  ],
  // Written in the §3.15 shape with the mirror ON — the common deal (one
  // contract, both products) as it is stored AFTER the reshape, so the demo
  // shows both representations side by side with the arena's legacy record.
  feeModelConfig: {
    transient: { feeModel: 'B', feeBps: 1500 },
    monthly: { sameAsTransient: true },
  },
  facilities: [
    { id: FAC_PARK, name: 'Stone Mountain Park', address: 'Main Gate, Hwy 78', city: 'Stone Mountain', state: 'GA' },
  ],
  passes: [
    { id: 'daily',     label: 'Daily parking pass',          sublabel: 'One vehicle, all day — enter & exit', amountCents: 2000 },
    { id: 'multi-car', label: 'Additional vehicle (same day)', sublabel: 'Second car in one transaction',       amountCents: 2500 },
  ],
  recurringPlanLabel: 'Annual parking pass',
  // Flat $40/yr annual pass regardless of lot.
  monthlyPlanCentsByType: { garage: 4000, surface: 4000 },
  upcomingEvents: [
    { title: 'Lasershow Spectacular — Mountaintop', daysFromNow: 3,  when: '9:30 PM' },
    { title: 'Yellow Daisy Festival',               daysFromNow: 12, when: '10:00 AM' },
  ],
  eventRates: [
    { idSuffix: 'evt-lasershow', eventTitle: 'Lasershow Spectacular Weekend', daysFromNow: 3,  durationDays: 0.15 },
    { idSuffix: 'evt-festival',  eventTitle: 'Yellow Daisy Festival',         daysFromNow: 12, durationDays: 3 },
  ],
  finance: {
    platformFeeBps: 250,
    // Normal day ≈ $45k / ~2,500 sessions; peak (event/big weekend) day ≈ $190k /
    // ~9,000 sessions. Over 30 days this sums to ~$2.0M / ~100k sessions.
    baseDailyCents: 4_200_000,
    baseDailyStepCents: 50_000,
    baseDailyJitterCents: 900_000,
    weekendLift: 1.25,
    peakEveryNDays: 7,
    peakBonusCents: 13_500_000,
    peakBonusStepCents: 90_000,
    peakBonusJitterCents: 2_000_000,
    baseSessions: 2_400,
    baseSessionsJitter: 220,
    peakSessions: 9_000,
    peakSessionsJitter: 800,
    todayPartialFactor: 0.45,
    zoneRevShare: [0.55, 0.30, 0.15],
    zoneSessionShare: [0.58, 0.30, 0.12],
    byChannel: [
      // Online pre-purchase dominates for an attraction; app + kiosk fill the rest.
      { channel: 'web',     revShare: 0.42, sessionShare: 0.43, refundShare: 0.42 },
      { channel: 'parkify', revShare: 0.43, sessionShare: 0.42, refundShare: 0.43 },
      { channel: 'kiosk',   revShare: 0.15, sessionShare: 0.15, refundShare: 0 },
    ],
    byFacility: [
      { facilityId: FAC_PARK, facilityName: 'Stone Mountain Park', revShare: 1.0, sessionShare: 1.0, zones: 3 },
    ],
    durationMix: [
      { bucket: '< 2h', share: 0.04 },
      { bucket: '2-5h', share: 0.14 },
      { bucket: '5-9h', share: 0.30 },
      { bucket: 'All day', share: 0.52 },
    ],
    hourOfDay: [0, 0, 0, 0, 0, 1, 3, 10, 28, 52, 74, 88, 92, 90, 80, 66, 54, 40, 30, 22, 14, 8, 3, 1],
    dayOfWeek: [220, 90, 95, 105, 130, 180, 260],
    // Annual-pass holders return often.
    retention: { uniqueShare: 0.55, returningShare: 0.48, returnRate: 0.48 },
    validations: { totalCodesActive: 3, sessionsWithDiscount: 1_240 },
    permits: { active: 180, expiringSoon: 12, expired: 4 },
    kpis: { avgDurationMin: 296, activeNow: 318, activeReservedCents: 636_000 },
    peakHour: { hour: 13, sessions: 880, label: '1 PM' },
    payoutInstant: { instantAvailableCents: 4_250_000, estFeeCents: 6375, externalAccountLast4: '8841' },
  },
}
